
import {
  defineComponent, computed, ref, onMounted, onUnmounted, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import { useStore } from '@/store';
import FlowRenderer from '@/components/flows/FlowRenderer.vue';
import CustomDatePicker from '@/components/fields/CustomDatePicker.vue';
import { useGlobalFlowAnalytics } from '@/composables/flows/useFlowAnalytics';
import {
  fetchWorkflowStatsTree,
  WorkflowStatsTreeResult,
  WorkflowStatsTreeFilter,
} from '@/composables/flows/useFlowApi';
import { IntervalDateEnum, TypeCampaignEnum } from '@/types';

// Convertit l'objet `{ interval, customDateRange }` de CustomDatePicker en
// bornes ISO YYYY-MM-DD attendues par l'API stats-tree. Aligné sur la
// logique de `getAnalyticsDateRange` (useFlowApi.ts) mais avec format
// YYYY-MM-DD au lieu de YYYYMMDDHH.
const intervalToIsoRange = (
  interval: IntervalDateEnum | 'empty' | string,
  customDateRange?: Date[] | null,
): { dateStart: string; dateEnd: string } => {
  const today = moment();
  const fmt = 'YYYY-MM-DD';
  switch (interval) {
    case IntervalDateEnum.LAST_DAY:
      return { dateStart: today.clone().subtract(1, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_3_DAYS:
      return { dateStart: today.clone().subtract(3, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_7_DAYS:
      return { dateStart: today.clone().subtract(7, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_14_DAYS:
      return { dateStart: today.clone().subtract(14, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_30_DAYS:
      return { dateStart: today.clone().subtract(30, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_3_MONTHS:
      return { dateStart: today.clone().subtract(3, 'months').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_12_MONTHS:
      return { dateStart: today.clone().subtract(12, 'months').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.CUSTOM_DATE_RANGE:
      if (customDateRange && customDateRange.length === 2 && customDateRange[0] && customDateRange[1]) {
        return {
          dateStart: moment(customDateRange[0]).format(fmt),
          dateEnd: moment(customDateRange[1]).format(fmt),
        };
      }
      return { dateStart: today.clone().subtract(30, 'days').format(fmt), dateEnd: today.format(fmt) };
    default:
      // 'empty' ou inconnu → 30 derniers jours par défaut.
      return { dateStart: today.clone().subtract(30, 'days').format(fmt), dateEnd: today.format(fmt) };
  }
};

const formatInstanceDate = (iso: string, locale: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default defineComponent({
  name: 'WorkflowStatsFile',
  components: { FlowRenderer, CustomDatePicker },

  setup() {
    const { t, locale } = useI18n();
    const store = useStore();
    const flowAnalytics = useGlobalFlowAnalytics();

    const selectedData = computed(() => store.getters['dataExplorer/getSelectedData']);

    // Période initiale : on s'aligne sur le filtre date du rapport
    // statistique courant (`statsEditor/getPageFilters.date_interval`) si
    // disponible — l'utilisateur arrive ici via le kebab d'un tableau
    // stats et s'attend à voir la même fenêtre. Fallback 30 derniers
    // jours si le store n'a pas de date_interval (ouverture hors contexte
    // stats ou store non initialisé).
    const initialDateInterval = ((): {
      interval: IntervalDateEnum | 'empty' | string;
      customDateRange?: Date[];
    } => {
      const pageFilters = store.getters['statsEditor/getPageFilters'];
      const fromStore = pageFilters?.date_interval?.value;
      if (fromStore?.interval) {
        return {
          interval: fromStore.interval,
          ...(fromStore.customDateRange ? { customDateRange: fromStore.customDateRange } : {}),
        };
      }
      return { interval: IntervalDateEnum.LAST_30_DAYS };
    })();
    const dateInterval = ref<{ interval: IntervalDateEnum | 'empty' | string; customDateRange?: Date[] }>(
      initialDateInterval,
    );
    const initialRange = intervalToIsoRange(
      initialDateInterval.interval,
      initialDateInterval.customDateRange,
    );
    const dateStart = ref<string>(initialRange.dateStart);
    const dateEnd = ref<string>(initialRange.dateEnd);

    const filters = ref<WorkflowStatsTreeFilter>({
      ...((selectedData.value?.initialFilter as WorkflowStatsTreeFilter | null) ?? {}),
    });

    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const result = ref<WorkflowStatsTreeResult | null>(null);

    // Sélecteur de parcours (mode contact multi-instances). Initialement
    // synchronisé sur `result.selectedInstanceId` (= la plus récente par
    // défaut côté backend), puis pilote le refetch sur changement.
    const selectedInstanceId = ref<number | null>(null);
    const hasMultipleInstances = computed(
      () => (result.value?.availableInstances?.length ?? 0) > 1,
    );

    const idWorkflow = computed(() => Number(selectedData.value?.idWorkflow ?? 0));
    const type = computed<'automation' | 'bulk'>(
      () => (selectedData.value?.type as 'automation' | 'bulk') ?? 'automation',
    );

    // FlowRenderer attend un CampaignType (enum), pas un string literal.
    const rendererType = computed(() => (
      result.value?.workflow.type === 'bulk'
        ? TypeCampaignEnum.BULK
        : TypeCampaignEnum.AUTOMATION
    ));

    const load = async (overrideInstanceId?: number | null) => {
      if (!idWorkflow.value) return;
      isLoading.value = true;
      error.value = null;
      try {
        // Construit le filtre en injectant idWorkflowInstance quand on a
        // une sélection explicite (changement manuel du dropdown). Quand
        // overrideInstanceId est null (= reset), on retire le paramètre
        // pour laisser le backend re-déterminer le défaut.
        const effectiveFilter: WorkflowStatsTreeFilter = { ...filters.value };
        if (overrideInstanceId !== undefined && overrideInstanceId !== null) {
          effectiveFilter.idWorkflowInstance = overrideInstanceId;
        }
        result.value = await fetchWorkflowStatsTree(
          type.value,
          idWorkflow.value,
          dateStart.value,
          dateEnd.value,
          effectiveFilter,
        );
      } catch (err: any) {
        error.value = err?.message ?? t('workflowStats.errorGeneric');
        result.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    // Quand le backend renvoie un selectedInstanceId, on synchronise la
    // ref locale pour aligner le dropdown sur la sélection effective.
    watch(
      () => result.value?.selectedInstanceId,
      (val) => {
        if (val) selectedInstanceId.value = val;
        else selectedInstanceId.value = null;
      },
    );

    const onInstanceChange = () => {
      if (selectedInstanceId.value) load(selectedInstanceId.value);
    };

    // Watch sur l'intervalle pour resync dateStart/dateEnd + refetch.
    // CustomDatePicker emit `update:modelValue` à chaque change (preset
    // cliqué OU custom range complété). Le watch initial-callback est
    // skip via { immediate: false } — load() sera appelé une fois au
    // mount (onMounted ci-dessous).
    watch(
      dateInterval,
      (val) => {
        const { dateStart: ds, dateEnd: de } = intervalToIsoRange(
          val?.interval ?? 'empty',
          // primevue tape les dates en Date-like (sans Symbol.toPrimitive)
          // dans le v-model du Calendar — cast nécessaire pour l'aligner
          // sur la signature `Date[]` du helper.
          val?.customDateRange as Date[] | undefined,
        );
        // Skip si rien n'a changé (évite refetch redondant lorsque le
        // composant re-emit le même payload).
        if (ds === dateStart.value && de === dateEnd.value) return;
        dateStart.value = ds;
        dateEnd.value = de;
        selectedInstanceId.value = null;
        load();
      },
      { deep: true },
    );

    const onIntervalChange = () => {
      // Hook prévu par CustomDatePicker mais on garde la logique dans le
      // watch ci-dessus pour couvrir aussi l'init.
    };

    // Compteur pratique pour le template (état vide quand contact mode +
    // 0 step matché).
    const analyticsStepCount = computed(
      () => Object.keys(result.value?.analyticsByStep ?? {}).length,
    );

    const formatInstanceDateLocal = (iso: string) => formatInstanceDate(iso, locale.value);

    // Synchronise le singleton analytics avec les données reçues.
    // FlowRenderer/FlowNode lisent ces données via useGlobalFlowAnalytics().
    watch(result, (val) => {
      if (val) {
        const analyticsKeys = Object.keys(val.analyticsByStep ?? {});
        const flow: any = val.workflow?.flow ?? {};
        const steps: any[] = Array.isArray(flow.steps) ? flow.steps : [];
        // eslint-disable-next-line no-console
        console.log('[WSTREE-DEBUG] front received', {
          filterMode: val.filterMode,
          contactInfo: val.contactInfo,
          analyticsKeysCount: analyticsKeys.length,
          analyticsKeys,
          flowStepsCount: steps.length,
          rootStepId: flow.rootStepId,
          flowSteps: steps.map((s: any) => ({
            id: s?.id,
            type: s?.type,
            class: s?.class,
            primaryParentId: s?.primaryParent?.id,
            childrenIds: Array.isArray(s?.children)
              ? s.children.map((c: any) => c?.id)
              : [],
            hasMatchInAnalytics: !!val.analyticsByStep?.[s?.id],
          })),
          rawFlowKeys: Object.keys(flow),
        });
        flowAnalytics.enableAnalyticsMode();
        flowAnalytics.setAnalyticsData(val.analyticsByStep ?? {});
      }
    });

    const reload = () => load();
    const applyFilters = () => {
      // Nouveau filtre user = re-déterminer le défaut côté backend.
      selectedInstanceId.value = null;
      load();
    };
    const resetFilters = () => {
      filters.value = {};
      selectedInstanceId.value = null;
      load();
    };
    const clearContactFilter = () => {
      filters.value = {};
      selectedInstanceId.value = null;
      load();
    };

    onMounted(() => {
      load();
    });

    onUnmounted(() => {
      // Réinitialise le singleton pour ne pas polluer l'éditeur de scénario.
      flowAnalytics.setAnalyticsData({});
      flowAnalytics.disableAnalyticsMode();
    });

    return {
      t,
      dateStart,
      dateEnd,
      dateInterval,
      onIntervalChange,
      analyticsStepCount,
      filters,
      isLoading,
      error,
      result,
      rendererType,
      selectedInstanceId,
      hasMultipleInstances,
      reload,
      applyFilters,
      resetFilters,
      clearContactFilter,
      onInstanceChange,
      formatInstanceDate: formatInstanceDateLocal,
    };
  },
});
